import React, { useState, useEffect, useRef } from 'react';
import { View, ScrollView, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { useNavigate } from 'react-router-dom';
import '../components/CommonButton.css'; // Importa stili comuni per i pulsanti
import UserMinusIcon from '../components/UserMinusIcon';
import UserPlusIcon from '../components/UserPlusIcon';
import ScrollToTopButton from '../components/ScrollToTopButton';
import RefreshIcon from '../components/RefreshIcon';
import { generatePoolsAndMatches } from '../utils/individualUtils';
import { mergeIndividualMatches } from '../utils/mergeIndividualMatches';
import {
  FencingSwordsIcon,
  CustomSettingsIcon,
  ChartBarIcon
} from '../components/FencingIcons';

const theme = {
  primary: '#1976d2',
  secondary: '#388e3c',
  accent: '#ffe082',
  danger: '#d32f2f',
  bg: '#f7fafd',
  border: '#e0e3ef'
};

// Stili comuni sia per individuale che per squadre
const styles = StyleSheet.create({
  container: {
    backgroundColor: '#f7fafd',
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    padding: 16
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 18,
    boxShadow: '0 4px 24px #0002',
    padding: 16,
    marginVertical: 12,
    borderWidth: 2,
    borderColor: '#1976d2',
    transition: 'box-shadow 0.2s',
    maxWidth: '100vw',
    overflowX: 'auto',
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#1976d2',
    marginBottom: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  input: {
    borderWidth: 2,
    borderColor: '#b3c6e6',
    borderRadius: 8,
    padding: 10,
    marginBottom: 10,
    backgroundColor: '#f4faff',
    fontWeight: 'bold',
    fontSize: 16,
    transition: 'border-color 0.2s',
  },
  inputFocus: {
    borderColor: '#1976d2',
    backgroundColor: '#fff',
  },
  button: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 24,
    marginVertical: 8,
    fontWeight: 'bold',
    fontSize: 17,
    elevation: 2,
    shadowColor: '#1976d2',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.18,
    shadowRadius: 4,
  },
  btnPrimary: {
    backgroundColor: '#1976d2',
    color: '#fff',
  },
  btnSecondary: {
    backgroundColor: '#388e3c',
    color: '#fff',
  },
  btnDanger: {
    backgroundColor: '#d32f2f',
    color: '#fff',
  },
  btnText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 17,
    textAlign: 'center',
  },
  membersTitle: {
    fontWeight: 'bold',
    marginTop: 10,
    marginBottom: 4,
    color: '#388e3c',
    fontSize: 15
  },
  memberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    flexWrap: 'nowrap',
    maxWidth: '100%',
    overflow: 'hidden',
  },
  info: {
    fontSize: 14,
    color: '#666',
    marginBottom: 10
  }
});

const IndividualCompetitionConfig = () => {
  const navigate = useNavigate();

  const [numPools, setNumPools] = useState(1);
  const [schema, setSchema] = useState('manuale');
  const [gironiManuali, setGironiManuali] = useState([['']]);
  const [sbarramento, setSbarramento] = useState(0);
  const [stoccateGirone, setStoccateGirone] = useState('5');
  const [stoccateElimDiretta, setStoccateElimDiretta] = useState('12');
  const [feedback, setFeedback] = useState({ message: '', type: '' });

  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [selectedAtleta, setSelectedAtleta] = useState({ gironeIdx: null, atletaIdx: null });
  // Variabili rimosse (inutilizzate)

  const [caricamentoCompleto, setCaricamentoCompleto] = useState(false);

  useEffect(() => {
    console.log("CARICAMENTO INIZIALE CONFIGURAZIONE INDIVIDUALE");
    let gironi = [['']];
    try {
      const teamsRaw = localStorage.getItem('teams');
      if (teamsRaw) {
        const teams = JSON.parse(teamsRaw);
        console.log("TEAMS RAW:", teams);
        if (Array.isArray(teams) && teams.length > 0) {
          let maxPoolIdx = 0;
          teams.forEach(t => {
            const poolIdx = typeof t.pool === 'number' ? t.pool : 0;
            if (poolIdx > maxPoolIdx) maxPoolIdx = poolIdx;
          });
          gironi = Array(maxPoolIdx + 1).fill().map(() => []);
          teams.forEach(t => {
            const poolIdx = typeof t.pool === 'number' ? t.pool : 0;
            if (Array.isArray(t.members) && t.members.length > 0) {
              t.members.forEach(m => {
                const nome = typeof m === 'string' ? m : (m && m.name ? m.name : '');
                if (nome && nome.trim()) {
                  gironi[poolIdx].push(nome.trim());
                  console.log(`Aggiunto membro ${nome} al girone ${poolIdx}`);
                }
              });
            }
          });
          gironi = gironi.map(g => g.length === 0 ? [''] : g);
          console.log("GIRONI FINALI:", gironi);
        }
      }
    } catch (e) {
      console.error("Errore nel caricamento dei team:", e);
    }
    const numPools = Math.max(1, gironi.length);
    setGironiManuali(gironi);
    setNumPools(numPools);
    setSchema('manuale');
    setSbarramento(parseInt(localStorage.getItem('individual_sbarramento') || '0', 10));
    setStoccateGirone(localStorage.getItem('individual_stoccateGirone') || '5');
    setStoccateElimDiretta(localStorage.getItem('individual_stoccateElimDiretta') || '12');
    setCaricamentoCompleto(true); // <--- SOLO ORA è pronto
  }, []);

  useEffect(() => {
    if (!caricamentoCompleto) return;
    localStorage.setItem('individual_numPools', numPools);
    localStorage.setItem('individual_schema', schema);
    localStorage.setItem('individual_manual_pools', JSON.stringify(gironiManuali));
    localStorage.setItem('individual_sbarramento', sbarramento);
    localStorage.setItem('individual_stoccateGirone', stoccateGirone);
    localStorage.setItem('individual_stoccateElimDiretta', stoccateElimDiretta);
    console.log('Salvando configurazione in localStorage', {
      numPools,
      schema,
      gironiManuali,
      sbarramento,
      stoccateGirone,
      stoccateElimDiretta
    });
  }, [caricamentoCompleto, numPools, schema, gironiManuali, sbarramento, stoccateGirone, stoccateElimDiretta]);

  useEffect(() => {
    if (!caricamentoCompleto) return;
    setGironiManuali(prevGironi => {
      let newGironi = [...prevGironi];
      if (numPools > newGironi.length) {
        for (let i = newGironi.length; i < numPools; i++) {
          newGironi.push(['']);
        }
      } else if (numPools < newGironi.length) {
        newGironi = newGironi.slice(0, numPools);
      }
      return newGironi;
    });
  }, [caricamentoCompleto, numPools]);

  useEffect(() => {
    if (caricamentoCompleto) {
      localStorage.setItem('individual_numPools', numPools);
      localStorage.setItem('individual_schema', schema);
      localStorage.setItem('individual_manual_pools', JSON.stringify(gironiManuali));
      localStorage.setItem('individual_sbarramento', sbarramento);
      localStorage.setItem('individual_stoccateGirone', stoccateGirone);
      localStorage.setItem('individual_stoccateElimDiretta', stoccateElimDiretta);
      console.log('Salvando configurazione in localStorage', {
        numPools,
        schema,
        gironiManuali,
        sbarramento,
        stoccateGirone,
        stoccateElimDiretta
      });
    }
  }, [caricamentoCompleto, numPools, schema, gironiManuali, sbarramento, stoccateGirone, stoccateElimDiretta]);

  const initialLoad = useRef(true);

  useEffect(() => {
    if (initialLoad.current) {
      setTimeout(() => {
        initialLoad.current = false;
      }, 100);
    }
  }, []);

  useEffect(() => {
    if (initialLoad.current) return; // Blocca durante il primo caricamento
    setGironiManuali(prevGironi => {
      let newGironi = [...prevGironi];
      if (numPools > newGironi.length) {
        for (let i = newGironi.length; i < numPools; i++) {
          newGironi.push(['']);
        }
      } else if (numPools < newGironi.length) {
        newGironi = newGironi.slice(0, numPools);
      }
      return newGironi;
    });
  }, [numPools]);

  useEffect(() => {
    console.log('RENDER gironiManuali:', gironiManuali);
  }, [gironiManuali]);

  // Funzioni per gestire gli atleti nei gironi (corrette per immutabilità)
  const updateAtletaInGirone = (gironeIdx, atletaIdx, value) => {
    const newGironi = gironiManuali.map(g => [...g]); // Copia profonda di livello 1
    if (!newGironi[gironeIdx]) newGironi[gironeIdx] = [];
    newGironi[gironeIdx][atletaIdx] = value;
    setGironiManuali(newGironi);
  };

  const addAtletaToGirone = (gironeIdx, value) => {
    const newGironi = gironiManuali.map(g => [...g]);
    if (!newGironi[gironeIdx]) newGironi[gironeIdx] = [];
    newGironi[gironeIdx].push(value);
    setGironiManuali(newGironi);
  };

  // removeAtletaFromGirone rimossa (inutilizzata)

  // Log di verifica dopo ogni modifica
  useEffect(() => {
    console.log('POST-UPDATE gironiManuali:', gironiManuali);
  }, [gironiManuali]);

  const increment = (setter, val, min = 1, max = 99) => setter(Math.min(max, (parseInt(val, 10) || 0) + 1));
  const decrement = (setter, val, min = 0) => setter(Math.max(min, (parseInt(val, 10) || 0) - 1));

  // handleCleanAllData rimossa (inutilizzata)

  const handleProceed = () => {
    // Prepara gli atleti in base al numero di gironi
    // Validazione: tutti i nomi devono essere non vuoti e almeno due atleti presenti
    const nomiMancanti = gironiManuali.some(pool => pool.some(n => typeof n !== 'string' || !n.trim()));
    const numeroAtleti = gironiManuali.reduce((acc, pool) => acc + pool.length, 0);
    if (nomiMancanti || numeroAtleti < 2) {
      setFeedback({ message: 'Inserisci almeno due atleti/nome mancante', type: 'error' });
      return;
    }

    // --- PATCH: merge intelligente incontri ---
    const oldTeams = JSON.parse(localStorage.getItem('teams') || '[]');
    const oldMatches = JSON.parse(localStorage.getItem('matches') || '[]');

    // Salva una copia originale per debug
    localStorage.setItem('_backup_old_teams', JSON.stringify(oldTeams));
    localStorage.setItem('_backup_old_matches', JSON.stringify(oldMatches));

    // Converti la struttura, gestisci matches con mergeIndividualMatches
    const { allTeams, allMatches } = aggiornaIncontriConMerge(
      gironiManuali,
      numPools,
      oldMatches,
      oldTeams
    );
    // --- END PATCH ---

    // Salva dati in localStorage per persistenza
    localStorage.setItem('competitionType', 'individual');
    localStorage.setItem('individual_stoccateGirone', stoccateGirone);
    localStorage.setItem('individual_stoccateElimDiretta', stoccateElimDiretta);
    localStorage.setItem('teams', JSON.stringify(allTeams));
    localStorage.setItem('matches', JSON.stringify(allMatches));

    // Aggiungi un ritardo prima della navigazione per assicurarsi che i dati siano salvati
    setTimeout(() => {
      navigate('/schedule', {
        state: { teams: allTeams, matches: allMatches, stoccateGirone }
      });
    }, 200);
  };

  // Funzione per gestire lo spostamento tra gironi
  // handleMoveBetweenPools rimosso (inutilizzato)

  // Funzioni per DraggableBetweenLists
  // handleGironiManualiChange rimosso (inutilizzato)

  const handleNumPoolsChange = (value) => {
    const num = parseInt(value, 10) || 1;
    setNumPools(Math.max(1, num));
  };

  const handleIncreaseNumPools = () => {
    const newNumPools = numPools + 1;
    setNumPools(newNumPools);
  };

  const handleDecreaseNumPools = () => {
    if (numPools <= 1) return;

    const newNumPools = numPools - 1;
    setNumPools(newNumPools);
  };

  // Funzioni per sbarramento
  const handleSbarramentoChange = (value) => {
    const num = parseInt(value, 10) || 0;
    setSbarramento(Math.max(0, num));
  };

  const handleIncreaseSbarramento = () => {
    increment(setSbarramento, sbarramento, 0);
  };

  const handleDecreaseSbarramento = () => {
    decrement(setSbarramento, sbarramento, 0);
  };

  // Funzione per creare la competizione
  const handleCreateCompetition = () => {
    // Reset dei dati locali prima di creare una nuova competizione
    resetIndividualConfig();

    // Procedi con la nuova competizione
    handleProceed();
  };

  const openMoveMenu = (gironeIdx, atletaIdx, event) => {
    // Memorizza l'atleta selezionato
    setSelectedAtleta({ gironeIdx, atletaIdx });

    // Mostra il menu
    setShowMoveMenu(true);
  };

  const closeMoveMenu = () => {
    setShowMoveMenu(false);
  };

  const moveAtletaToGirone = (targetGironeIdx) => {
    const { gironeIdx, atletaIdx } = selectedAtleta;

    // Assicurati che l'atleta e il girone di destinazione siano validi
    if (gironeIdx === null || atletaIdx === null || gironeIdx === targetGironeIdx) {
      closeMoveMenu();
      return;
    }

    // Crea una copia dei gironi
    const newGironi = [...gironiManuali];

    // Ottieni l'atleta da spostare
    const atletaDaSpostare = newGironi[gironeIdx][atletaIdx];

    // Rimuovi l'atleta dal girone originale
    newGironi[gironeIdx].splice(atletaIdx, 1);

    // Se il girone originale è vuoto, aggiungi un campo vuoto
    if (newGironi[gironeIdx].length === 0) {
      newGironi[gironeIdx].push('');
    }

    // Aggiungi l'atleta al nuovo girone
    newGironi[targetGironeIdx].push(atletaDaSpostare);

    // Aggiorna lo stato
    setGironiManuali(newGironi);

    // Chiudi il menu
    closeMoveMenu();
  };

  // Funzione di RESET configurazione individuale (da chiamare quando si crea una nuova gara)
  const resetIndividualConfig = () => {
    localStorage.removeItem('individual_numPools');
    localStorage.removeItem('individual_manual_pools');
    localStorage.removeItem('individual_schema');
    localStorage.removeItem('individual_sbarramento');
    localStorage.removeItem('individual_stoccateGirone');
    localStorage.removeItem('individual_stoccateElimDiretta');
    // Pulisci anche la chiave generica per evitare conflitti
    localStorage.removeItem('stoccatePerAssalto');
  };

  // Gestione cambio valore stoccate eliminazione diretta
  const handleStoccateElimDirettaChange = (val) => {
    setStoccateElimDiretta(val);
    localStorage.setItem('individual_stoccateElimDiretta', val);
  };

  // Funzioni per incrementare e decrementare stoccate eliminazione diretta
  const handleIncreaseStoccateElimDiretta = () => {
    const newValue = parseInt(stoccateElimDiretta) + 1;
    if (!isNaN(newValue) && newValue <= 99) {
      handleStoccateElimDirettaChange(newValue.toString());
    }
  };

  const handleDecreaseStoccateElimDiretta = () => {
    const newValue = parseInt(stoccateElimDiretta) - 1;
    if (!isNaN(newValue) && newValue >= 1) {
      handleStoccateElimDirettaChange(newValue.toString());
    }
  };

  // Gestione cambio valore stoccate girone
  const handleStoccateGironeChange = (val) => {
    setStoccateGirone(val);
    localStorage.setItem('individual_stoccateGirone', val);
  };

  // Funzioni per incrementare e decrementare stoccate girone
  const handleIncreaseStoccateGirone = () => {
    const newValue = parseInt(stoccateGirone) + 1;
    if (!isNaN(newValue) && newValue <= 99) {
      handleStoccateGironeChange(newValue.toString());
    }
  };

  const handleDecreaseStoccateGirone = () => {
    const newValue = parseInt(stoccateGirone) - 1;
    if (!isNaN(newValue) && newValue >= 1) {
      handleStoccateGironeChange(newValue.toString());
    }
  };

  // --- PATCH: merge intelligente incontri individuali ---
  const aggiornaIncontriConMerge = (nuoviPartecipanti, numPools, oldMatches, oldTeams) => {
    console.log("===== INIZIO ASSEGNAZIONE ID STABILI =====");

    // Recupero il registry esistente per debug
    const stableIdRegistry = JSON.parse(localStorage.getItem('_stable_id_registry') || '{}');
    console.log("ID stabili registrati:", stableIdRegistry);

    // Prima salviamo una lista dei vecchi team con i loro ID stabili
    const vecchiTeamConId = [];
    if (oldTeams && oldTeams.length > 0) {
      oldTeams.forEach(team => {
        if (team && team.members && team.members[0]) {
          console.log(`Vecchio team: ${team.members[0]} con ID: ${team.stableId || 'non definito'}`);
          vecchiTeamConId.push({
            nome: team.members[0],
            stableId: team.stableId
          });
        }
      });
    }

    // Per ogni nuovo partecipante, controlla se esiste già un vecchio team con lo stesso nome
    // e riutilizza lo stesso ID stabile
    const mappingStableId = {};

    // Flatten dei nuovi partecipanti per l'analisi
    const tuttiPartecipanti = [];
    nuoviPartecipanti.forEach(girone => {
      girone.forEach(partecipante => {
        if (typeof partecipante === 'string' && partecipante.trim() !== '') {
          tuttiPartecipanti.push(partecipante);
        }
      });
    });

    console.log("Tutti i nuovi partecipanti:", tuttiPartecipanti);

    // Crea una copia dei nuovi partecipanti con solo nomi per generatePoolsAndMatches
    const nuoviPartecipantiStringhe = nuoviPartecipanti.map(girone => {
      return girone.map(item => {
        const nome = typeof item === 'string' ? item.trim() : '';
        if (nome === '') return nome;

        // Cerca il partecipante nei vecchi team
        const vecchioTeam = vecchiTeamConId.find(vt => vt.nome === nome);

        if (vecchioTeam && vecchioTeam.stableId) {
          // Riutilizza l'ID stabile esistente
          console.log(`Riutilizzo ID stabile per ${nome}: ${vecchioTeam.stableId}`);
          mappingStableId[nome] = vecchioTeam.stableId;
        } else {
          // Crea un nuovo ID stabile
          const idStabile = getOrCreateStableId(nome);
          console.log(`Nuovo ID stabile per ${nome}: ${idStabile}`);
          mappingStableId[nome] = idStabile;
        }

        return nome; // Restituisci solo la stringa del nome
      });
    });

    // Genera nuova struttura incontri con solo le stringhe dei nomi
    const { allTeams, allMatches } = generatePoolsAndMatches(nuoviPartecipantiStringhe, numPools);

    // Aggiungi gli ID stabili ai team generati
    allTeams.forEach(team => {
      if (team.members && team.members[0]) {
        const nome = team.members[0];
        team.stableId = mappingStableId[nome] || getOrCreateStableId(nome);
        console.log(`Team generato: ${nome} con ID: ${team.stableId}`);
      }
    });

    // Debug dettagliato dei team e loro ID
    console.log("===== TEAMS GENERATI CON ID STABILI =====");
    allTeams.forEach((team, idx) => {
      console.log(`Team[${idx}]: ${team.members?.[0] || 'unnamed'} - ID stabile: ${team.stableId || 'mancante!'}`);
    });

    // Applica il merge intelligente usando gli ID stabili
    console.log("===== INIZIO MERGE MATCHES =====");
    const mergedMatches = mergeIndividualMatches(oldMatches, allMatches, allTeams);

    return { allTeams, allMatches: mergedMatches };
  };

  // Funzione di supporto per ottenere o creare ID stabili
  const getOrCreateStableId = (name) => {
    if (!name) return null;

    // Carica il registro degli ID stabili
    let stableIdRegistry = JSON.parse(localStorage.getItem('_stable_id_registry') || '{}');

    // Cerca un ID esistente per questo nome
    const existingId = Object.keys(stableIdRegistry).find(id =>
      stableIdRegistry[id].name === name
    );

    if (existingId) {
      return existingId;
    } else {
      // Crea un nuovo ID stabile
      const newId = `athlete-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      stableIdRegistry[newId] = { name, createdAt: Date.now() };
      localStorage.setItem('_stable_id_registry', JSON.stringify(stableIdRegistry));
      return newId;
    }
  };

  // Funzione per assegnare ID stabili ai tiratori
  // assignStableIds rimossa (inutilizzata)

  // Mostra un messaggio di caricamento finché i dati dei gironi non sono coerenti
  const gironiReady = gironiManuali && gironiManuali.length > 0 && gironiManuali.every(g => Array.isArray(g));

  if (!gironiReady || gironiManuali.length < numPools || gironiManuali.some(g => g.length === 0)) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <Text style={{ fontSize: 18, color: '#888' }}>Caricamento gironi...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView>
        {/* Titolo nello stesso stile di MatchSchedule */}
        <View style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', marginTop: 10 }}>
          <View style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            {/* Icona schermidore */}
            <FencingSwordsIcon style={{ fontSize: 36, color: theme.primary, marginRight: 12 }} />
            {/* Titolo */}
            <Text style={{ fontSize: 32, fontWeight: 'bold', color: theme.primary, margin: 0, textAlign: 'center', letterSpacing: 1, textShadow: '0px 2px 4px rgba(0,0,0,0.06)' }}>
              Individuale
            </Text>
          </View>
          {/* Separatore blu */}
          <View style={{ width: 72, height: 4, backgroundColor: theme.primary, borderRadius: 2, marginTop: 2, marginBottom: 18, opacity: 0.2 }} />
        </View>
        {/* Feedback */}
        {feedback.message ? (
          <View style={{
            padding: 12,
            borderRadius: 8,
            marginBottom: 16,
            backgroundColor: feedback.type === 'error' ? '#ffebee' : '#e8f5e9',
            borderLeftWidth: 4,
            borderLeftColor: feedback.type === 'error' ? '#d32f2f' : '#388e3c'
          }}>
            <Text style={{ color: feedback.type === 'error' ? '#d32f2f' : '#388e3c' }}>{feedback.message}</Text>
          </View>
        ) : null}

        {/* Configurazione */}
        <View style={styles.card}>
          <Text style={styles.title}>
            <CustomSettingsIcon style={{ fontSize: 24, color: theme.primary, marginRight: 8 }} /> Impostazioni Generali
          </Text>

          <View style={{ marginBottom: 16 }}>
            <Text style={{ fontWeight: 'bold', marginBottom: 4, color: theme.primary }}>
              Stoccate per Incontro (Girone)
            </Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'nowrap', maxWidth: '100%', overflow: 'hidden' }}>
              <TextInput
                style={{ ...styles.input, flex: 1, minWidth: 0, width: '100%', flexShrink: 1, marginRight: 4, fontSize: 15, padding: 8 }}
                value={stoccateGirone}
                onChangeText={handleStoccateGironeChange}
                keyboardType="numeric"
                placeholder="5"
              />

              <TouchableOpacity
                style={{ ...styles.button, ...styles.btnPrimary, paddingVertical: 6, paddingHorizontal: 12, marginRight: 4 }}
                onPress={handleDecreaseStoccateGirone}
              >
                <Text style={styles.btnText}>-</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={{ ...styles.button, ...styles.btnPrimary, paddingVertical: 6, paddingHorizontal: 12 }}
                onPress={handleIncreaseStoccateGirone}
              >
                <Text style={styles.btnText}>+</Text>
              </TouchableOpacity>
            </View>
          </View>

          <View style={{ marginBottom: 16 }}>
            <Text style={{ fontWeight: 'bold', marginBottom: 4, color: theme.primary }}>
              Stoccate per Incontro (Eliminazione Diretta)
            </Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'nowrap', maxWidth: '100%', overflow: 'hidden' }}>
              <TextInput
                style={{ ...styles.input, flex: 1, minWidth: 0, width: '100%', flexShrink: 1, marginRight: 4, fontSize: 15, padding: 8 }}
                value={stoccateElimDiretta}
                onChangeText={handleStoccateElimDirettaChange}
                keyboardType="numeric"
                placeholder="12"
              />

              <TouchableOpacity
                style={{ ...styles.button, ...styles.btnPrimary, paddingVertical: 6, paddingHorizontal: 12, marginRight: 4 }}
                onPress={handleDecreaseStoccateElimDiretta}
              >
                <Text style={styles.btnText}>-</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={{ ...styles.button, ...styles.btnPrimary, paddingVertical: 6, paddingHorizontal: 12 }}
                onPress={handleIncreaseStoccateElimDiretta}
              >
                <Text style={styles.btnText}>+</Text>
              </TouchableOpacity>
            </View>
          </View>

          <View style={{ marginBottom: 16 }}>
            <Text style={{ fontWeight: 'bold', marginBottom: 4, color: theme.primary }}>
              Numero di Gironi
            </Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'nowrap', maxWidth: '100%', overflow: 'hidden' }}>
              <TextInput
                style={{ ...styles.input, flex: 1, minWidth: 0, width: '100%', flexShrink: 1, marginRight: 4, fontSize: 15, padding: 8 }}
                value={numPools.toString()}
                onChangeText={handleNumPoolsChange}
                keyboardType="numeric"
              />

              <TouchableOpacity
                style={{ ...styles.button, ...styles.btnPrimary, paddingVertical: 6, paddingHorizontal: 12, marginRight: 4 }}
                onPress={handleDecreaseNumPools}
              >
                <Text style={styles.btnText}>-</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={{ ...styles.button, ...styles.btnPrimary, paddingVertical: 6, paddingHorizontal: 12 }}
                onPress={handleIncreaseNumPools}
              >
                <Text style={styles.btnText}>+</Text>
              </TouchableOpacity>
            </View>
          </View>

          <View style={{ marginBottom: 16 }}>
            <Text style={{ fontWeight: 'bold', marginBottom: 4, color: theme.primary }}>
              Sbarramento
            </Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'nowrap', maxWidth: '100%', overflow: 'hidden' }}>
              <TextInput
                style={{ ...styles.input, flex: 1, minWidth: 0, width: '100%', flexShrink: 1, marginRight: 4, fontSize: 15, padding: 8 }}
                value={sbarramento.toString()}
                onChangeText={handleSbarramentoChange}
                keyboardType="numeric"
              />

              <TouchableOpacity
                style={{ ...styles.button, ...styles.btnPrimary, paddingVertical: 6, paddingHorizontal: 12, marginRight: 4 }}
                onPress={handleDecreaseSbarramento}
              >
                <Text style={styles.btnText}>-</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={{ ...styles.button, ...styles.btnPrimary, paddingVertical: 6, paddingHorizontal: 12 }}
                onPress={handleIncreaseSbarramento}
              >
                <Text style={styles.btnText}>+</Text>
              </TouchableOpacity>
            </View>
            <Text style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              Numero di atleti che passano alla fase eliminatoria (0 = tutti)
            </Text>
          </View>

          {/* 
          <View style={{marginBottom: 16}}>
            <Text style={{fontWeight: 'bold', marginBottom: 8, color: theme.primary}}>
              Schema di Distribuzione Assalti
            </Text>
            
            <Text>Manuale</Text>
          </View>
          */}
        </View>

        {/* Sezione Gironi */}
        <View style={styles.card}>
          <Text style={styles.title}>
            <ChartBarIcon style={{ fontSize: 24, color: theme.primary, marginRight: 8 }} /> Composizione Gironi
          </Text>

          <View style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', padding: 0 }}>
            {gironiManuali.map((girone, gironeIdx) => (
              <View
                key={`girone-${gironeIdx}`}
                style={{
                  flex: 1,
                  minWidth: 250,
                  maxWidth: '95%',
                  width: '100%',
                  marginBottom: 16,
                  padding: 8,
                  backgroundColor: '#f9f9f9',
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  margin: '0 auto',
                  marginHorizontal: 'auto',
                  marginVertical: 8,
                  boxSizing: 'border-box',
                  overflow: 'hidden'
                }}
              >
                <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'nowrap', width: '100%' }}>
                  <Text style={{ fontWeight: 'bold', color: theme.primary, marginRight: 5, fontSize: 15 }}>Girone {gironeIdx + 1}</Text>
                  <TouchableOpacity
                    style={{
                      ...styles.button,
                      ...styles.btnPrimary,
                      paddingVertical: 5,
                      paddingHorizontal: 8,
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      right: 0,
                      minWidth: 'auto',
                      flexShrink: 0
                    }}
                    onPress={() => addAtletaToGirone(gironeIdx)}
                  >
                    <UserPlusIcon size={14} color="white" style={{ marginRight: 2 }} />
                    <Text style={{ ...styles.btnText, fontSize: 13, whiteSpace: 'nowrap' }}>Aggiungi</Text>
                  </TouchableOpacity>
                </View>

                {girone.map((atleta, atletaIdx) => (
                  <View
                    key={`atleta-${gironeIdx}-${atletaIdx}`}
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginBottom: 8,
                      padding: 4,
                      borderRadius: 4,
                      backgroundColor: 'white',
                      border: '1px solid #eee',
                      transition: 'all 0.2s'
                    }}
                  >
                    {gironiManuali.length > 1 && (
                      <TouchableOpacity
                        style={{
                          padding: 4,
                          marginRight: 8,
                          borderRadius: 4,
                        }}
                        onPress={(e) => openMoveMenu(gironeIdx, atletaIdx, e)}
                      >
                        <RefreshIcon size={20} color="#555" />
                      </TouchableOpacity>
                    )}
                    <TextInput
                      style={{ borderWidth: 2, borderColor: '#b3c6e6', borderRadius: 8, padding: 8, fontSize: 15, marginRight: 4, backgroundColor: '#f4faff', fontWeight: 'bold', flex: 1, minWidth: 0, maxWidth: '60vw', flexShrink: 1, transition: 'border-color 0.2s' }}
                      value={atleta}
                      onChangeText={(value) => updateAtletaInGirone(gironeIdx, atletaIdx, value)}
                      placeholder={`Atleta ${atletaIdx + 1}`}
                    />
                    <TouchableOpacity
                      style={{ padding: 8 }}
                      onPress={() => {
                        const newGironi = [...gironiManuali];
                        newGironi[gironeIdx].splice(atletaIdx, 1);
                        // Se il girone è vuoto, aggiungi un campo vuoto
                        if (newGironi[gironeIdx].length === 0) {
                          newGironi[gironeIdx].push('');
                        }
                        setGironiManuali(newGironi);
                      }}
                    >
                      <UserMinusIcon size={24} color="#d32f2f" />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            ))}
          </View>
        </View>

        {/* Feedback errore sopra i pulsanti */}
        {feedback.type === 'error' && feedback.message && (
          <View style={{
            backgroundColor: '#fff5f5',
            borderColor: '#d32f2f',
            borderWidth: 1.5,
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
            alignItems: 'center'
          }}>
            <Text style={{ color: '#d32f2f', fontWeight: 'bold', fontSize: 15, textAlign: 'center' }}>{feedback.message}</Text>
          </View>
        )}
        {/* Pulsante "Genera Competizione" */}
        <View className="button-container">
          <TouchableOpacity
            style={{ ...styles.button, ...styles.btnSecondary }}
            className="common-button success"
            onPress={handleCreateCompetition}
          >
            <Text style={styles.btnText}>Genera Competizione</Text>
          </TouchableOpacity>
        </View>

        {/* Pulsante "Aggiorna" */}
        <View className="button-container">
          <TouchableOpacity
            style={{ ...styles.button, ...styles.btnPrimary }}
            className="common-button"
            onPress={handleProceed}
          >
            <Text style={styles.btnText}>Aggiorna</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      <ScrollToTopButton threshold={300} />

      {/* Menu a comparsa per lo spostamento degli atleti */}
      {showMoveMenu && (
        <View
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000
          }}
          onClick={closeMoveMenu}
        >
          <View
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'white',
              borderRadius: 4,
              boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
              padding: 0,
              minWidth: 250,
              maxWidth: '80%',
              zIndex: 1001
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Text style={{
              padding: 12,
              fontWeight: 'bold',
              borderBottom: '1px solid #eee',
              backgroundColor: '#f5f5f5',
              textAlign: 'center',
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4
            }}>
              Sposta in girone:
            </Text>
            <View style={{ maxHeight: '70vh', overflow: 'auto', padding: 8 }}>
              {gironiManuali.map((_, targetGironeIdx) =>
                targetGironeIdx !== selectedAtleta.gironeIdx && (
                  <TouchableOpacity
                    key={`menu-move-to-${targetGironeIdx}`}
                    style={{
                      padding: 12,
                      borderBottom: '1px solid #f5f5f5',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onPress={() => moveAtletaToGirone(targetGironeIdx)}
                  >
                    <Text style={{ fontSize: 16 }}>Girone {targetGironeIdx + 1}</Text>
                  </TouchableOpacity>
                )
              )}
            </View>
            <View style={{
              padding: 12,
              borderTop: '1px solid #eee',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <TouchableOpacity
                style={{
                  paddingVertical: 8,
                  paddingHorizontal: 16,
                  backgroundColor: '#f44336',
                  borderRadius: 4
                }}
                onPress={closeMoveMenu}
              >
                <Text style={{ color: 'white', fontWeight: 'bold' }}>Annulla</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

export function resetIndividualConfig() {
  localStorage.removeItem('individual_numPools');
  localStorage.removeItem('individual_manual_pools');
  localStorage.removeItem('individual_schema');
  localStorage.removeItem('individual_sbarramento');
  localStorage.removeItem('individual_stoccateGirone');
  localStorage.removeItem('individual_stoccateElimDiretta');
  // Pulisci anche la chiave generica per evitare conflitti
  localStorage.removeItem('stoccatePerAssalto');

  // Pulisci il registro degli ID stabili per evitare riferimenti a tiratori cancellati
  localStorage.removeItem('_stable_id_registry');
  console.log('[DEBUG] Registro ID stabili resettato durante la creazione della nuova competizione');
}

export default IndividualCompetitionConfig;
